'use client';

import { useState } from 'react';
import { useTranslations } from '@/i18n/intl';
import { sendMessage } from '@/lib/actions/messages';
import { toast } from 'sonner';

interface ServiceContactFormProps {
  serviceTitle: string;
}

export function ServiceContactForm({ serviceTitle }: ServiceContactFormProps) {
  const t = useTranslations('Expertise');
  const tForm = useTranslations('Form');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const result = await sendMessage({
        name: formData.name,
        email: formData.email,
        subject: `Inquiry: ${serviceTitle}`,
        message: formData.message || `Interested in ${serviceTitle}`,
      });

      if (result.success) {
        toast.success(tForm('success'));
        setFormData({ name: '', email: '', message: '' });
      } else {
        toast.error(tForm('error'));
      }
    } catch (error) {
      toast.error(tForm('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white p-6 rounded-xl border border-light shadow-sm">
      <h3 className="text-[20px] font-bold text-navy mb-2 font-heading">
        {t('discussNeeds')}
      </h3>
      <p className="text-[14px] text-gray mb-6 font-sans">
        {t('consultExperts')}
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={tForm('name')}
            className="w-full bg-light border-b border-navy/20 px-4 py-2 text-[14px] text-navy focus:outline-none focus:border-primary transition-colors font-sans"
            required
          />
        </div>
        <div>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={tForm('email')}
            className="w-full bg-light border-b border-navy/20 px-4 py-2 text-[14px] text-navy focus:outline-none focus:border-primary transition-colors font-sans"
            required
          />
        </div>
        <div>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder={tForm('message')}
            rows={3}
            className="w-full bg-light border-b border-navy/20 px-4 py-2 text-[14px] text-navy focus:outline-none focus:border-primary transition-colors resize-none font-sans"
          />
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-primary text-white text-[14px] font-semibold py-3 rounded-full hover:bg-navy transition-all duration-300 disabled:opacity-50 font-sans uppercase"
        >
          {isLoading ? tForm('submitting').toUpperCase() : tForm('submit').toUpperCase()}
        </button>
      </form>
    </div>
  );
}
